/**
 * Onboarding typed state + localStorage persistence.
 *
 * Lives in its own file (not `types/index.ts`) so the user-staged onboarding
 * copy changes in `types/index.ts` are not disturbed by this work. A future
 * slice can re-export from the barrel once the user-owned edits have been
 * committed.
 */

import { ONBOARDING_TOTAL_STEPS } from "./index";

/**
 * Typed sentinel for the onboarding-only fallback comuna option
 * ("No está tu comuna?"). It is intentionally outside the range of real
 * `City.id` values (which are positive integers) so the discriminant
 * cannot be confused with a real selection.
 */
export const ONBOARDING_FALLBACK_COMUNA_ID = -1;

/**
 * Label rendered for the fallback comuna option in the Comuna dropdown.
 * Exposed as a constant so consumers (tests, copy, accessibility text)
 * share the same source of truth.
 */
export const ONBOARDING_FALLBACK_COMUNA_LABEL = "No está tu comuna?";

/**
 * Typed purchase-frequency selection captured on step 4 of the onboarding
 * wizard. The Spanish display labels (Diario, Semanal, Mensual, Otra) are
 * mapped onto this enum so the persisted payload uses stable English keys
 * independent of the copy shown in the UI.
 */
export type PurchaseFrequency = "daily" | "weekly" | "monthly" | "other";

const PURCHASE_FREQUENCY_VALUES: readonly PurchaseFrequency[] = [
  "daily",
  "weekly",
  "monthly",
  "other",
];

/**
 * Coerces an arbitrary value into the typed `PurchaseFrequency` union.
 *
 * Any value outside the declared enum (including `undefined`, `null`,
 * non-string values, or unknown strings) collapses to `null` so the
 * persisted payload always stays in sync with the `OnboardingState`
 * contract — even when the storage slot was tampered with manually. Used
 * by both the writer (defense-in-depth) and the navigation hook's hydrator
 * so a single source of truth decides what counts as valid.
 */
/**
 * Coerces an arbitrary value into a valid `habitual_store_id` number or null.
 *
 * A valid habitual store id is a positive finite integer (matching the
 * backend's `user_onboarding_data.habitual_store_id` DDL column). Returns
 * `null` for:
 *   - `null` / `undefined` (no favorite selected yet)
 *   - non-number values (`string`, `object`, `array`, `boolean`)
 *   - non-finite numbers (`NaN`, `±Infinity`)
 *   - zero or negative numbers
 *   - non-integer floats
 *
 * Mirrors the `normalizePurchaseFrequency` pattern so the writer and the
 * navigation hydrator share a single source of truth for what counts as a
 * valid habitual store. Exported for unit testing.
 */
export const normalizeHabitualStoreId = (raw: unknown): number | null => {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0 || !Number.isInteger(raw)) {
    return null;
  }
  return raw;
};

export const normalizePurchaseFrequency = (
  raw: unknown,
): PurchaseFrequency | null => {
  if (typeof raw !== "string") {
    return null;
  }
  return PURCHASE_FREQUENCY_VALUES.includes(raw as PurchaseFrequency)
    ? (raw as PurchaseFrequency)
    : null;
};

/**
 * Coerces an arbitrary value into a valid `monthly_spend` number.
 *
 * Returns `null` for any value that is not a positive finite number:
 *   - `null` / `undefined` (e.g. field missing in a tampered payload)
 *   - non-number values (`string`, `object`, `array`, `boolean`)
 *   - non-finite numbers (`NaN`, `±Infinity`)
 *   - zero or negative numbers
 *
 * Mirrors the `normalizePurchaseFrequency` pattern so the writer and the
 * navigation hydrator share a single source of truth for what counts as
 * a valid spend. Exported for unit testing.
 */
export const normalizeMonthlySpend = (raw: unknown): number | null => {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) {
    return null;
  }
  return raw;
};

/**
 * Coerces an arbitrary value into a list of numeric `store_ids`.
 *
 * Filters out anything that is not a finite integer (non-numbers,
 * `NaN`, `±Infinity`, non-integer floats, `null`, `undefined`). The
 * returned array preserves the input order and never contains
 * duplicates. Missing or non-array inputs collapse to `[]`.
 *
 * Used by both the writer (defense-in-depth) and the navigation
 * hydrator so a single source of truth decides what counts as a valid
 * store id. Exported for unit testing.
 */
export const normalizeStoreIds = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<number>();
  const result: number[] = [];
  for (const entry of raw) {
    if (
      typeof entry === "number" &&
      Number.isFinite(entry) &&
      Number.isInteger(entry)
    ) {
      if (!seen.has(entry)) {
        seen.add(entry);
        result.push(entry);
      }
    }
  }
  return result;
};

/**
 * Coerces an arbitrary value into a list of numeric `subscription_ids`.
 *
 * Membership selections travel as numeric `suscriptions.id` values (the
 * canonical identifier the backend persists into
 * `user_suscriptions.suscription_id`). The frontend obtains these ids
 * from the `id` field that the public
 * `/settings/store_preferences_public` response now exposes alongside
 * the legacy `text_key` field — see `app/models/store.py::MemberShips`
 * and `app/api/endpoints/settings.py::get_store_preferences_public`.
 *
 * Filtering rules: keeps finite positive integers, drops everything else
 * (strings, `NaN`, `±Infinity`, floats, `null`, `undefined`, non-integers).
 * De-duplicates while preserving order. Missing or non-array inputs
 * collapse to `[]`.
 *
 * Exported for unit testing.
 */
export const normalizeSubscriptionIds = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<number>();
  const result: number[] = [];
  for (const entry of raw) {
    if (
      typeof entry === "number" &&
      Number.isFinite(entry) &&
      Number.isInteger(entry) &&
      entry > 0
    ) {
      if (!seen.has(entry)) {
        seen.add(entry);
        result.push(entry);
      }
    }
  }
  return result;
};

/**
 * localStorage key used to persist the in-progress onboarding wizard.
 * Separate from `userData` so the onboarding flow never collides with
 * the global app location state.
 */
export const ONBOARDING_STORAGE_KEY = "carriapp:onboarding";

/**
 * Shape of the onboarding payload persisted to localStorage.
 *
 * Field names use the underscore form (`city_id`, `location_id`, etc.)
 * to match the backend / `userData` contract.
 *
 * Cut-2 extension: `store_ids` and `subscription_ids` carry the step-3
 * supermarket and membership selections made on
 * `OnboardingStorePreferences`. They are mirrored into the
 * `POST /api/users/onboarding` payload by `buildSaveOnboardingPayload`
 * so the backend can persist `user_store_preference` and
 * `user_suscriptions`.
 *
 * - `store_ids: number[]` mirrors the numeric `StorePreference.id` from
 *   `/settings/store_preferences_public` and matches the backend
 *   `OnboardingRequest.store_ids: List[int]` field.
 * - `subscription_ids: number[]` carries the numeric `suscriptions.id`
 *   values that `/settings/store_preferences_public` now exposes in
 *   `MemberShips.id` (alongside the legacy `text_key` label). The
 *   backend `OnboardingRequest.subscription_ids: List[int]` field
 *   expects numeric ids; sending them verbatim keeps the persistence
 *   contract aligned end-to-end.
 */
export interface OnboardingState {
  currentStep: number;
  city_id: number | null;
  city_name: string | null;
  location_id: number | null;
  location_name: string | null;
  isFallback: boolean;
  purchase_frequency: PurchaseFrequency | null;
  monthly_spend: number | null;
  store_ids: number[];
  subscription_ids: number[];
  /** The user's usual/favorite store id (`StorePreference.id`), or `null` when
   *  the user has not selected a favorite on step 3. Persisted into
   *  `user_onboarding_data.habitual_store_id` by the backend. */
  habitual_store_id: number | null;
}

/**
 * Reads and validates the persisted onboarding payload.
 *
 * Returns `null` when:
 *   - the storage key is missing
 *   - the stored value is not valid JSON
 *   - the parsed value is not an object
 *
 * Partial / malformed objects are returned as-is so the caller can decide
 * how to recover. Use `writeOnboardingState` to guarantee shape integrity.
 *
 * The returned object always carries `store_ids: number[]` and
 * `subscription_ids: number[]` (defaulting to `[]` when absent) so
 * downstream consumers can rely on a stable shape regardless of
 * whether the persisted payload was written by the current build or a
 * legacy build that predates the arrays. Legacy payloads that stored
 * text_key strings collapse to `[]` — text_keys are no longer accepted
 * on read so the persisted state stays in sync with the backend
 * numeric contract.
 */
export const readOnboardingState = (): OnboardingState | null => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  const raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") {
      return null;
    }
    const candidate = parsed as Partial<OnboardingState>;
    return {
      currentStep: typeof candidate.currentStep === "number" ? candidate.currentStep : 1,
      city_id: typeof candidate.city_id === "number" ? candidate.city_id : null,
      city_name:
        typeof candidate.city_name === "string" ? candidate.city_name : null,
      location_id:
        typeof candidate.location_id === "number"
          ? candidate.location_id
          : null,
      location_name:
        typeof candidate.location_name === "string"
          ? candidate.location_name
          : null,
      isFallback: candidate.isFallback === true,
      purchase_frequency: normalizePurchaseFrequency(
        candidate.purchase_frequency,
      ),
      monthly_spend: normalizeMonthlySpend(candidate.monthly_spend),
      store_ids: normalizeStoreIds(candidate.store_ids),
      subscription_ids: normalizeSubscriptionIds(candidate.subscription_ids),
      habitual_store_id: normalizeHabitualStoreId(candidate.habitual_store_id),
    };
  } catch {
    return null;
  }
};

/**
 * Persists an onboarding payload to localStorage.
 *
 * No-op when `window.localStorage` is not available (e.g. SSR / test
 * environments without a DOM). Throws nothing — the helper is safe to
 * call from event handlers.
 *
 * `purchase_frequency` is normalized through `normalizePurchaseFrequency`
 * so any unexpected enum value stored by a tampered payload collapses to
 * `null` instead of leaking an unknown string into the safe shape.
 *
 * `store_ids` and `subscription_ids` are normalized through their own
 * helpers so a tampered payload that holds garbage (non-numeric
 * entries, duplicates, non-positive integers) collapses to clean
 * arrays before reaching localStorage. The `subscription_ids` helper
 * only accepts positive finite integers — the legacy `string[]`
 * text_key storage shape is no longer accepted so the writer cannot
 * leak text_keys into a numeric-only contract.
 */
export const writeOnboardingState = (state: OnboardingState): void => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  const clampedStep = Math.max(
    1,
    Math.min(ONBOARDING_TOTAL_STEPS, Math.trunc(state.currentStep) || 1),
  );
  const safeState: OnboardingState = {
    currentStep: clampedStep,
    city_id: state.city_id,
    city_name: state.city_name,
    location_id: state.location_id,
    location_name: state.location_name,
    isFallback: state.isFallback,
    purchase_frequency: normalizePurchaseFrequency(state.purchase_frequency),
    monthly_spend: normalizeMonthlySpend(state.monthly_spend),
    store_ids: normalizeStoreIds(state.store_ids),
    subscription_ids: normalizeSubscriptionIds(state.subscription_ids),
    habitual_store_id: normalizeHabitualStoreId(state.habitual_store_id),
  };
  window.localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(safeState));
};

/**
 * Removes the persisted onboarding draft from localStorage.
 *
 * Called by `usePersistOnboardingData` ONLY after a successful
 * `POST /api/users/onboarding` so the draft survives a network failure and
 * can be retried on the next `/store` visit. No-op when `window.localStorage`
 * is unavailable or the key is already missing — safe to call from a fire-
 * and-forget effect.
 */
export const clearOnboardingState = (): void => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  window.localStorage.removeItem(ONBOARDING_STORAGE_KEY);
};

/** Re-export the total step count so consumers can drive state clamping. */
export { ONBOARDING_TOTAL_STEPS };
